<template>
    <div class="marketing-detail-container">
        <o-bread-crumb>
            <template v-slot:pageName>Marketing</template>
            <template v-slot:pageDetail>{{ marketing?.name || 'Detail' }}</template>
            <template v-slot:pageDescription>
                <button class="btn-back" @click="goBack">
                    <i class="fas fa-arrow-left"></i> Back to List
                </button>
            </template>
        </o-bread-crumb>

        <!-- Feature Disabled State -->
        <div v-if="!isMarketingModuleEnabled" class="feature-disabled-container">
            <div class="disabled-content">
                <i class="fas fa-toggle-off fa-3x primary-text mb-3"></i>
                <h4>Marketing Module Disabled</h4>
                <p>The Marketing Module has been disabled by System Administration. Please contact System Owner to enable this feature.</p>
            </div>
        </div>

        <template v-if="isMarketingModuleEnabled">
        <div v-if="validation" class="alert alert-danger" role="alert">
            <i class="fas fa-exclamation-circle me-2"></i>{{ validation }}
        </div>

        <div v-show="page.isLoading" class="loading-state">
            <div class="spinner-border primary-text" role="status"></div>
            <p class="loading-text">Loading marketing details...</p>
        </div>

        <div v-if="!page.isLoading && marketing" class="detail-content">
            <!-- Marketing Info Card -->
            <div class="info-card">
                <div class="info-header">
                    <div class="info-header-left">
                        <i class="fas fa-info-circle"></i>
                        <h5>Marketing Information</h5>
                    </div>
                </div>
                <div class="info-body">
                    <div class="info-grid">
                        <div class="info-item">
                            <label>Name</label>
                            <div class="info-value">
                                <i class="fas fa-bullhorn primary-text"></i>
                                <span>{{ marketing.name }}</span>
                            </div>
                        </div>
                        <div class="info-item">
                            <label>Description</label>
                            <div class="info-value">
                                <i class="fas fa-align-left text-muted"></i>
                                <span>{{ marketing.description || '-' }}</span>
                            </div>
                        </div>
                        <div class="info-item" v-if="!isAssignedUser">
                            <label>Created By</label>
                            <div class="info-value">
                                <i class="fas fa-user primary-text"></i>
                                <span>{{ marketing.user?.first_name }} {{ marketing.user?.last_name }}</span>
                            </div>
                        </div>
                        <div class="info-item" v-if="!isAssignedUser">
                            <label>Assigned To</label>
                            <div class="info-value">
                                <i class="fas fa-user-check primary-text"></i>
                                <span>{{ marketing.assigned_user?.first_name }} {{ marketing.assigned_user?.last_name }}</span>
                            </div>
                        </div>
                        <div class="info-item">
                            <label>Created At</label>
                            <div class="info-value">
                                <i class="fas fa-calendar primary-text"></i>
                                <span>{{ formatDate(marketing.created_at) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Campaigns Card -->
            <div class="campaigns-card">
                <div class="campaigns-header">
                    <div class="campaigns-header-left">
                        <i class="fas fa-rocket"></i>
                        <h5>Campaigns</h5>
                        <span class="campaign-count">{{ campaigns.length }}</span>
                    </div>
                    <button 
                        @click="showCreateCampaignModal" 
                        class="btn-primary-action"
                        v-if="campaignWrite"
                    >
                        <i class="fas fa-plus"></i>
                        <span>Create Campaign</span>
                    </button>
                </div>
                
                <div class="campaigns-body">
                    <!-- Filter Bar for Campaigns -->
                    <div class="filter-action-wrapper mb-3 mt-3">
                        <div class="filter-controls">
                            <!-- Search by Name -->
                            <div class="filter-item">
                                <div class="section-label-tabs">
                                    <span class="label-tab">Search</span>
                                </div>
                                <div class="date-range-box">
                                    <div class="search-input-wrapper">
                                        <i class="fas fa-search search-icon-filter"></i>
                                        <input
                                            v-model="searchCampaignName"
                                            type="text"
                                            placeholder="Search by name..."
                                            class="form-control-filter"
                                            @keyup.enter="applyCampaignFilter"
                                        />
                                        <button v-if="searchCampaignName" class="btn-clear-input" @click="searchCampaignName = ''" title="Clear">
                                            <i class="fas fa-times"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Period Filter -->
                            <div class="filter-item">
                                <div class="section-label-tabs">
                                    <span class="label-tab">Period</span>
                                </div>
                                <div class="date-range-box">
                                    <select class="form-select form-control-filter" v-model="selectedTimeScale" @change="handleTimeScaleChange">
                                        <option value="">All Time</option>
                                        <option value="day">Today</option>
                                        <option value="week">This Week</option>
                                        <option value="month">This Month</option>
                                        <option value="custom">Custom Range</option>
                                    </select>
                                </div>
                            </div>

                            <!-- Date Range Picker (only for custom) -->
                            <div class="filter-item" v-show="selectedTimeScale === 'custom'">
                                <div class="section-label-tabs">
                                    <span class="label-tab">Date Range</span>
                                </div>
                                <div class="date-range-box">
                                    <div class="date-input-wrapper">
                                        <input
                                            placeholder="Select Date Range"
                                            ref="datePicker"
                                            class="form-control-filter"
                                        />
                                        <i class="fas fa-calendar-alt calendar-icon-filter"></i>
                                    </div>
                                </div>
                            </div>

                            <!-- Sort By -->
                            <div class="filter-item">
                                <div class="section-label-tabs">
                                    <span class="label-tab">Sort By</span>
                                </div>
                                <div class="date-range-box">
                                    <select class="form-select form-control-filter" v-model="selectedSortBy">
                                        <option value="created_at">Created Date</option>
                                        <option value="name">Name</option>
                                        <option value="total_events">Total Events</option>
                                        <option value="total_members">Total Members</option>
                                    </select>
                                </div>
                            </div>

                            <!-- Sort Direction -->
                            <div class="filter-item">
                                <div class="section-label-tabs">
                                    <span class="label-tab">Sort</span>
                                </div>
                                <div class="date-range-box">
                                    <div class="sort-toggle-box">
                                        <span class="sort-label" :class="{ active: !isDesc }">ASC</span>
                                        <label class="switch-sm">
                                            <input type="checkbox" v-model="isDesc">
                                            <span class="slider-sm round"></span>
                                        </label>
                                        <span class="sort-label" :class="{ active: isDesc }">DESC</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="action-section">
                            <div class="action-buttons-group">
                                <button @click="applyCampaignFilter" class="btn btn-apply-filter">
                                    <i class="fas fa-filter me-1"></i> Apply
                                </button>
                                <button v-if="isCampaignFilterApplied" @click="clearCampaignFilter" class="btn btn-clear-filter">
                                    <i class="fas fa-times me-1"></i> Clear
                                </button>
                                <button class="btn-refresh" @click.prevent="syncData()" :disabled="isSyncing" title="Sync Data & Refresh">
                                    <i class="fas fa-sync-alt" :class="{ 'fa-spin': isSyncing }"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div v-if="campaigns.length === 0" class="empty-state">
                        <i class="fas fa-inbox fa-3x"></i>
                        <h5>No Campaigns Yet</h5>
                        <p>Create your first campaign to start tracking!</p>
                    </div>

                    <div v-else class="table-wrapper">
                        <o-table style="text-align:left;" class="table-hover report-list-table">
                            <thead>
                                <tr>
                                    <td class="fw-bolder text-muted" scope="col">No</td>
                                    <td class="fw-bolder text-muted" scope="col">Name</td>
                                    <td class="fw-bolder text-muted" scope="col">Business Type</td>
                                    <td class="fw-bolder text-muted" scope="col">Business</td>
                                    <td class="fw-bolder text-muted" scope="col">Code</td>
                                    <td class="fw-bolder text-muted text-center" scope="col">Total Events</td>
                                    <td class="fw-bolder text-muted text-center" scope="col">Total Members</td>
                                    <td class="fw-bolder text-muted" scope="col">Start Date</td>
                                    <td class="fw-bolder text-muted" scope="col">End Date</td>
                                    <td class="fw-bolder text-muted text-center" scope="col">Action</td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(campaign, index) in campaigns" :key="campaign.id" class="report-row-clickable" @click="goToCampaignDetail(campaign.id)">
                                    <td><span class="date-text">{{ (campaignMeta.page - 1) * campaignMeta.limit + index + 1 }}</span></td>
                                    <td>
                                        <i class="fas fa-rocket primary-text me-2"></i>
                                        <span class="merchant-text fw-semibold">{{ campaign.name }}</span>
                                        <span class="status-badge-small ms-2" :class="'status-' + campaign.status">{{ getCampaignStatusLabel(campaign.status) }}</span>
                                    </td>
                                    <td>
                                        <span class="badge-type">{{ getBusinessTypeName(campaign.businessable_type) }}</span>
                                    </td>
                                    <td>
                                        <span class="branch-text">{{ campaign.businessable?.name || '-' }}</span>
                                    </td>
                                    <td>
                                        <code class="referral-code">{{ campaign.code }}</code>
                                    </td>
                                    <td class="text-center">
                                        <div v-if="isSyncing" class="spinner-border spinner-border-sm primary-text mx-auto d-block" role="status"></div>
                                        <span v-else class="amount-positive-text">{{ campaign.total_events ?? 0 }}</span>
                                    </td>
                                    <td class="text-center">
                                        <div v-if="isSyncing" class="spinner-border spinner-border-sm primary-text mx-auto d-block" role="status"></div>
                                        <span v-else class="member-count-text">{{ campaign.total_members ?? 0 }}</span>
                                    </td>
                                    <td><span class="date-text">{{ formatDate(campaign.validity_start) }}</span></td>
                                    <td><span class="date-text">{{ formatDate(campaign.validity_end) }}</span></td>
                                    <td class="text-center">
                                        <div class="action-buttons" @click.stop>
                                            <button class="btn-action btn-action-analytics" @click.stop="goToCampaignDetail(campaign.id)" title="View Analytics">
                                                <i class="fas fa-chart-line"></i>
                                            </button>
                                            <button class="btn-action btn-action-view" @click.stop="viewCampaign(campaign)" title="Quick View">
                                                <i class="fas fa-eye"></i>
                                            </button>
                                            <button class="btn-action btn-action-edit" v-if="campaignWrite" @click.stop="editCampaign(campaign)" title="Edit">
                                                <i class="fas fa-edit"></i>
                                            </button>
                                            <button class="btn-action btn-action-delete" v-if="campaignWrite" @click.stop="confirmDeleteCampaign(campaign)" title="Delete">
                                                <i class="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-if="campaigns.length === 0">
                                    <td colspan="10" class="text-center">No campaign data found</td>
                                </tr>
                            </tbody>
                        </o-table>
                        <!-- Summary Footer -->
                        <div class="total-summary mt-4 mb-3">
                            <div class="row g-3">
                                <div class="col-md-6">
                                    <div class="summary-card">
                                        <div class="summary-content">
                                            <div class="summary-icon icon-events">
                                                <i class="fas fa-calendar-check"></i>
                                            </div>
                                            <div class="summary-details">
                                                <p class="summary-label-text">Total Events</p>
                                                <h3 class="summary-value-text total-events">
                                                    {{ formatNumber(totalEventsCount) }}
                                                </h3>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="summary-card">
                                        <div class="summary-content">
                                            <div class="summary-icon icon-members">
                                                <i class="fas fa-users"></i>
                                            </div>
                                            <div class="summary-details">
                                                <p class="summary-label-text">Total Members Registered</p>
                                                <h3 class="summary-value-text total-members">
                                                    {{ formatNumber(totalMembersCount) }}
                                                </h3>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Pagination -->
                        <div class="pagination-wrapper mt-4 mb-2">
                            <o-pagination :page="campaignMeta.page" :total="campaignMeta.total" @callback="navCampaignPage"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Campaign Form Modal -->
        <campaign-form-new
            v-if="isCampaignModalVisible"
            ref="campaignFormRef"
            :editedItem="editedCampaign"
            :marketingId="marketingId"
            @save="saveCampaign"
            @close="closeCampaignModal"
        ></campaign-form-new>

        <!-- Campaign View Modal -->
        <o-modal v-if="isViewModalVisible" @close="closeViewModal" modal-class="show d-block modal-wide">
            <template v-slot:header>
                <div class="modal-header-with-badge">
                    <h5 class="modal-title">
                        <i class="fas fa-rocket primary-text me-2"></i>{{ viewedCampaign?.name || 'Campaign Details' }}
                    </h5>
                    <span class="status-badge-modal" :class="'status-' + viewedCampaign?.status">
                        {{ getCampaignStatusLabel(viewedCampaign?.status) }}
                    </span>
                </div>
            </template>
            <template v-slot:body>
                <div v-if="viewedCampaign" class="campaign-details-wide">
                    <!-- Campaign Info Grid -->
                    <div class="detail-section">
                        <div class="detail-grid-two">
                            <div class="detail-item-modern">
                                <label>
                                    <i class="fas fa-building"></i>
                                    Business Type
                                </label>
                                <span class="badge-type-clean">{{ getBusinessTypeName(viewedCampaign.businessable_type) }}</span>
                            </div>
                            <div class="detail-item-modern">
                                <label>
                                    <i class="fas fa-briefcase"></i>
                                    Business
                                </label>
                                <span class="value-text">{{ viewedCampaign.businessable?.name || '-' }}</span>
                            </div>
                            <div class="detail-item-modern">
                                <label>
                                    <i class="fas fa-calendar-alt"></i>
                                    Validity Period
                                </label>
                                <span class="value-text">{{ formatDate(viewedCampaign.validity_start) }} - {{ formatDate(viewedCampaign.validity_end) }}</span>
                            </div>
                            <div class="detail-item-modern">
                                <label>
                                    <i class="fas fa-link"></i>
                                    Target Link
                                </label>
                                <a :href="viewedCampaign.target_link" target="_blank" class="link-external-clean">
                                    {{ viewedCampaign.target_link || '-' }}
                                    <i class="fas fa-external-link-alt"></i>
                                </a>
                            </div>
                        </div>
                    </div>

                    <!-- Share Section -->
                    <div class="detail-section share-section">
                        <div class="share-row">
                            <div class="share-item">
                                <label>
                                    <i class="fas fa-link"></i>
                                    Referral Link
                                </label>
                                <div class="input-copy-group">
                                    <input 
                                        type="text" 
                                        :value="getReferralLink(viewedCampaign.code)" 
                                        readonly 
                                        class="input-clean"
                                        @click="selectReferralLink"
                                    />
                                    <button class="btn-copy-clean" @click="copyReferralLink(viewedCampaign.code)">
                                        <i class="fas fa-copy"></i>
                                        Copy Link
                                    </button>
                                </div>
                            </div>
                            <div class="share-item">
                                <label>
                                    <i class="fas fa-code"></i>
                                    Referral Code
                                </label>
                                <div class="input-copy-group">
                                    <code class="code-clean">{{ viewedCampaign.code }}</code>
                                    <button class="btn-copy-clean" @click="copyCode(viewedCampaign.code)">
                                        <i class="fas fa-copy"></i>
                                        Copy Code
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="share-info">
                            <i class="fas fa-info-circle"></i>
                            <span>Share this link to track member registrations</span>
                        </div>
                    </div>
                </div>
            </template>
        </o-modal>

        <!-- Delete Confirmation -->
        <o-warning-box
            v-if="isDeleteCampaignConfirmVisible"
            title="Delete Campaign"
            :item-name="campaignToDelete?.name || 'this campaign'"
            warning-message="This action cannot be undone."
            confirm-text="Yes, Delete"
            cancel-text="Cancel"
            @confirm="deleteCampaign"
            @cancel="closeDeleteCampaignConfirm"
        />
        </template>
    </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, reactive, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { OBreadCrumb, OTable, OWarningBox, OModal, OPagination } from '@/components/OmnihControl'
import { useToast } from '@/composables/useToast'
import CampaignFormNew from './CampaignFormNew.vue'
import api from '@/helpers/api'
import flatpickr from 'flatpickr'
import 'flatpickr/dist/flatpickr.css'

export default {
    name: 'MarketingDetail',
    components: {
        OBreadCrumb,
        OTable,
        OWarningBox,
        OModal,
        OPagination,
        CampaignFormNew
    },
    setup() {
        const store = useStore()
        const router = useRouter()
        const route = useRoute()

        const marketingId = ref(route.params.id)
        const marketing = ref(null)
        const campaigns = ref([])
        
        const page = reactive({
            isLoading: false
        })

        const campaignFields = ref(['No', 'Name', 'Business Type', 'Business', 'Code', 'Total Events', 'Total Members', 'Start Date', 'End Date', 'Action'])
        const validation = ref('')
        const write = ref(false)
        const campaignWrite = ref(false)
        const isAssignedUser = ref(false)
        const currentUserId = computed(() => store.state.Auth.currUser?.id)

        // Check if marketing module is enabled via feature flag
        const isMarketingModuleEnabled = computed(() => {
            return (store.state.FeatureFlag?.marketingModule?.flag || 'ACTIVE') === 'ACTIVE'
        })

        const campaignMeta = ref({
            page: 1,
            limit: 10,
            total: 0,
            total_events_sum: 0,
            total_members_sum: 0
        })

        const selectedTimeScale = ref('')
        const selectedSortBy = ref('created_at')
        const isDesc = ref(true)
        const isCampaignFilterApplied = ref(false)
        const searchCampaignName = ref('')
        const dateRange = ref([null, null])
        const datePicker = ref(null)
        let flatpickrInstance = null

        const isCampaignModalVisible = ref(false)
        const editedCampaign = ref(null)
        const isViewModalVisible = ref(false)
        const viewedCampaign = ref(null)
        const isDeleteCampaignConfirmVisible = ref(false)
        const campaignToDelete = ref(null)
        const isSyncing = ref(false)
        const referralLinkInput = ref(null)
        const campaignFormRef = ref(null)

        const { showToast } = useToast()

        const userInfo = computed(() => store.state.Auth.userInfo)

        onMounted(() => {
            loadMarketingDetail()
        })

        // Re-initialize flatpickr when custom date range is selected
        watch(selectedTimeScale, async (val) => {
            if (val === 'custom') {
                await nextTick()
                if (datePicker.value && !flatpickrInstance) {
                    flatpickrInstance = flatpickr(datePicker.value, {
                        mode: 'range',
                        maxRange: 31,
                        onChange: (selectedDates) => {
                            if (selectedDates.length === 2) {
                                dateRange.value = selectedDates
                            }
                        },
                    })
                }
            } else {
                if (flatpickrInstance) {
                    flatpickrInstance.destroy()
                    flatpickrInstance = null
                }
                dateRange.value = [null, null]
            }
        })

        onBeforeUnmount(() => {
            if (flatpickrInstance) {
                flatpickrInstance.destroy()
                flatpickrInstance = null
            }
        })

        watch(userInfo, (newUserInfo) => {
            if (newUserInfo) {
                checkPermissions()
            }
        }, { immediate: true })

        function checkPermissions() {
            if (store.state.Auth.currUser?.level === 'root') {
                write.value = true
                campaignWrite.value = true
                isAssignedUser.value = false
            } else {
                const userModules = store.state.Auth.userInfo?.modules
                if (userModules && Array.isArray(userModules)) {
                    const marketingModule = userModules.find(module => module.module_name === 'marketing')
                    const campaignModule = userModules.find(module => module.module_name === 'campaign')
                    
                    write.value = marketingModule ? marketingModule.write : false
                    campaignWrite.value = campaignModule ? campaignModule.write : false
                } else {
                    write.value = false
                    campaignWrite.value = false
                }
            }
        }

        function loadMarketingDetail() {
            page.isLoading = true
            validation.value = ''

            store.dispatch('Marketing/readById', marketingId.value)
                .then((response) => {
                    marketing.value = response.data?.data || response.data || response
                    loadCampaigns()

                    if (marketing.value.assigned_user_id === currentUserId.value) {
                        isAssignedUser.value = true
                    }
                })
                .catch((err) => {
                    validation.value = err.response?.data?.message || 'Failed to load marketing detail'
                    page.isLoading = false
                })
        }

        function loadCampaigns(timeFilter = {}) {
            const rawFilter = {
                marketing_id: marketingId.value,
                ...timeFilter,
            }

            const filter = {}
            Object.entries(rawFilter).forEach(([key, val]) => {
                if (val !== null && val !== undefined && val !== '') {
                    filter[key] = val
                }
            })

            const search = {}
            if (searchCampaignName.value) {
                search.name = searchCampaignName.value
            }

            const meta = { 
                page: campaignMeta.value.page, 
                limit: campaignMeta.value.limit, 
                sortBy: selectedSortBy.value, 
                sortAsc: !isDesc.value 
            }

            return api.request('get', '/api/campaign/', { meta, filter, search })
                .then((result) => {
                    campaigns.value = result.data?.data || []
                    if (result.data?.meta) {
                        campaignMeta.value.total = result.data.meta.total
                        campaignMeta.value.total_events_sum = result.data.meta.total_events_sum || 0
                        campaignMeta.value.total_members_sum = result.data.meta.total_members_sum || 0
                    }
                })
                .catch((err) => {
                    validation.value = err.response?.data?.message || 'Failed to load campaigns'
                })
                .finally(() => {
                    page.isLoading = false
                })
        }

        function handleTimeScaleChange() {
            dateRange.value = [null, null]
        }

        function applyCampaignFilter() {
            const timeFilter = {
                time_scale: selectedTimeScale.value || null,
                date_from: null,
                date_to: null,
            }

            if (selectedTimeScale.value === 'custom' && dateRange.value[0] && dateRange.value[1]) {
                timeFilter.date_from = formatDateToYYYYMMDD(dateRange.value[0])
                timeFilter.date_to  = formatDateToYYYYMMDD(dateRange.value[1])
            }

            isCampaignFilterApplied.value = true
            page.isLoading = true
            campaignMeta.value.page = 1
            loadCampaigns(timeFilter)
        }

        function syncData() {
            if (isSyncing.value) return
            
            isSyncing.value = true
            store.dispatch('Marketing/sync')
                .then(() => {
                    showToast({
                        type: 'success',
                        title: 'Sync Successful',
                        message: 'Marketing data has been synchronized with the latest events.',
                    })
                    loadMarketingDetail()
                })
                .catch((err) => {
                    showToast({
                        type: 'error',
                        title: 'Sync Failed',
                        message: err.response?.data?.message || 'Failed to sync marketing data.',
                    })
                })
                .finally(() => {
                    isSyncing.value = false
                })
        }

        function clearCampaignFilter() {
            selectedTimeScale.value = ''
            selectedSortBy.value = 'created_at'
            isDesc.value = true
            dateRange.value = [null, null]
            searchCampaignName.value = ''
            isCampaignFilterApplied.value = false

            if (flatpickrInstance) {
                flatpickrInstance.clear()
                if (flatpickrInstance.input) {
                    flatpickrInstance.input.value = ''
                }
            }

            campaignMeta.value.page = 1
            page.isLoading = true
            loadCampaigns()
        }

        function formatDateToYYYYMMDD(date) {
            const d = new Date(date)
            const year = d.getFullYear()
            const month = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        }

        function showCreateCampaignModal() {
            editedCampaign.value = null
            isCampaignModalVisible.value = true
        }

        function editCampaign(campaign) {
            editedCampaign.value = campaign
            isCampaignModalVisible.value = true
        }

        function closeCampaignModal() {
            isCampaignModalVisible.value = false
            editedCampaign.value = null
        }

        function saveCampaign(data) {
            const action = data.id ? 'Campaign/update' : 'Campaign/create'
            const isUpdate = !!data.id

            return new Promise((resolve, reject) => {
                store.dispatch(action, { data })
                    .then((response) => {
                        loadMarketingDetail()
                        closeCampaignModal()

                        const campaignName = response?.data?.data?.name || data.name || '-'

                        showToast({
                            type: 'success',
                            title: isUpdate ? 'Update Successful' : 'Creation Successful',
                            message: isUpdate
                                ? `Campaign "${campaignName}" has been successfully updated.`
                                : `Campaign "${campaignName}" has been successfully created.`,
                        })
                        resolve(response)
                    })
                    .catch((err) => {
                        const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to save campaign'
                        if (errorMessage === 'Referral code already exists.' && campaignFormRef.value) {
                            campaignFormRef.value.errors.code = errorMessage
                        }
                        validation.value = errorMessage
                        showToast({
                            type: 'error',
                            title: 'Save Failed',
                            message: validation.value,
                        })
                        reject(err)
                    })
            })
        }

        function viewCampaign(campaign) {
            viewedCampaign.value = campaign
            isViewModalVisible.value = true
        }

        function closeViewModal() {
            isViewModalVisible.value = false
            viewedCampaign.value = null
        }

        function confirmDeleteCampaign(campaign) {
            campaignToDelete.value = campaign
            isDeleteCampaignConfirmVisible.value = true
        }

        function closeDeleteCampaignConfirm() {
            isDeleteCampaignConfirmVisible.value = false
            campaignToDelete.value = null
        }

        function deleteCampaign() {
            if (!campaignToDelete.value) return

            const deletedName = campaignToDelete.value.name
            store.dispatch('Campaign/delete', campaignToDelete.value.id)
                .then(() => {
                    loadMarketingDetail()
                    closeDeleteCampaignConfirm()
                    showToast({
                        type: 'success',
                        title: 'Deletion Successful',
                        message: `Campaign "${deletedName}" has been successfully deleted.`,
                    })
                })
                .catch((err) => {
                    validation.value = err.response?.data?.message || 'Failed to delete campaign'
                    closeDeleteCampaignConfirm()
                    showToast({
                        type: 'error',
                        title: 'Deletion Failed',
                        message: validation.value,
                    })
                })
        }

        function getBusinessTypeName(type) {
            if (!type) return '-'
            const typeName = type.replace('App\\Models\\', '')
            return typeName
        }

        function formatDate(date) {
            if (!date) return '-'
            return new Date(date).toLocaleDateString('en-GB')
        }

        function getCampaignStatusLabel(status) {
            const labels = {
                'active': 'Active',
                'expired': 'Expired',
                'upcoming': 'Upcoming'
            }
            return labels[status] || 'Active'
        }

        function copyCode(code) {
            navigator.clipboard.writeText(code).then(() => {
                showToast({
                    type: 'success',
                    title: 'Copied!',
                    message: 'Referral code copied to clipboard.'
                })
            })
        }

        function getReferralLink(code) {
            const currentDomain = window.location.origin
            return `${currentDomain}/marketing?referral=${code}`
        }

        function copyReferralLink(code) {
            const link = getReferralLink(code)
            navigator.clipboard.writeText(link).then(() => {
                showToast({
                    type: 'success',
                    title: 'Copied!',
                    message: 'Referral link copied to clipboard.'
                })
            })
        }

        function selectReferralLink(event) {
            event.target.select()
        }

        function goBack() {
            router.push('/marketings');
        }

        function goToCampaignDetail(campaignId) {
            router.push({ name: 'CampaignDetail', params: { id: campaignId } })
        }

        function navCampaignPage(pageNum) {
            campaignMeta.value.page = pageNum
            page.isLoading = true
            loadCampaigns()
        }

        const totalEventsCount = computed(() => {
            return campaignMeta.value.total_events_sum
        })

        const totalMembersCount = computed(() => {
            return campaignMeta.value.total_members_sum
        })

        const formatNumber = (value) => {
            const intValue = parseInt(value, 10) || 0;
            return intValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        };

        return {
            marketingId,
            marketing,
            campaigns,
            page,
            campaignMeta,
            searchCampaignName,
            campaignFields,
            validation,
            write,
            campaignWrite,
            isAssignedUser,
            isCampaignModalVisible,
            editedCampaign,
            isViewModalVisible,
            viewedCampaign,
            isDeleteCampaignConfirmVisible,
            campaignToDelete,
            referralLinkInput,
            campaignFormRef,
            // Filter state
            datePicker,
            selectedTimeScale,
            selectedSortBy,
            isDesc,
            isCampaignFilterApplied,
            isSyncing,
            // Functions
            loadMarketingDetail,
            showCreateCampaignModal,
            editCampaign,
            closeCampaignModal,
            saveCampaign,
            viewCampaign,
            closeViewModal,
            confirmDeleteCampaign,
            closeDeleteCampaignConfirm,
            deleteCampaign,
            getBusinessTypeName,
            formatDate,
            getCampaignStatusLabel,
            copyCode,
            getReferralLink,
            copyReferralLink,
            selectReferralLink,
            goBack,
            goToCampaignDetail,
            // Filter functions
            applyCampaignFilter,
            clearCampaignFilter,
            handleTimeScaleChange,
            syncData,
            navCampaignPage,
            totalEventsCount,
            totalMembersCount,
            formatNumber,
            isMarketingModuleEnabled
        }
    }
}
</script>

<style scoped>
/* Feature Disabled State */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}

/* Container */
.marketing-detail-container {
    padding: 16px;
    padding-right: 20px;
    max-width: 100%;
}

/* Filter & Action Bar (TransactionReport pattern) */
.filter-action-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    flex-wrap: wrap;
    background-color: #fff;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.filter-controls {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
}

.filter-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 140px;
}

.section-label-tabs {
    display: flex;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
}

.label-tab {
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    color: var(--primary-color, #0d6efd);
    background: white;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.date-range-box {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 46px;
}

.form-control-filter {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 14px;
    height: 46px;
    min-width: 160px;
    background-color: white;
}

.form-control-filter:focus {
    border-color: var(--primary-color, #0d6efd);
    box-shadow: 0 0 0 0.25rem var(--secondary-color, rgba(13, 110, 253, 0.25));
    outline: none;
}

.date-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.date-input-wrapper .form-control-filter {
    padding-right: 36px;
    width: 220px;
}

/* Search Input Wrapper */
.search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.search-input-wrapper .form-control-filter {
    padding-left: 36px;
    padding-right: 36px;
    min-width: 200px;
}

.search-icon-filter {
    position: absolute;
    left: 12px;
    color: #94a3b8;
    pointer-events: none;
    z-index: 1;
}

.btn-clear-input {
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    font-size: 12px;
}

.btn-clear-input:hover {
    color: #64748b;
}

.calendar-icon-filter {
    position: absolute;
    right: 12px;
    color: var(--primary-color, #FC6666);
    pointer-events: none;
}

.sort-toggle-box {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 46px;
    padding: 0 8px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: white;
}

.sort-label {
    font-size: 12px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
}

.sort-label.active {
    color: var(--primary-color, #FC6666);
}

.switch-sm {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 22px;
}

.switch-sm input {
    opacity: 0;
    width: 0;
    height: 0;
}

.slider-sm {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .3s;
    border-radius: 22px;
}

.slider-sm:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: .3s;
    border-radius: 50%;
}

input:checked + .slider-sm {
    background-color: var(--primary-color, #FC6666);
}

input:checked + .slider-sm:before {
    transform: translateX(18px);
}

.action-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.action-buttons-group {
    display: flex;
    align-items: center;
    gap: 10px;
}

.btn-apply-filter {
    background-color: var(--primary-color, #FC6666);
    color: white;
    font-weight: 600;
    font-size: 13px;
    padding: 0 20px;
    height: 40px;
    display: flex;
    align-items: center;
    border: none;
    border-radius: 8px;
}

.btn-apply-filter:hover {
    filter: brightness(0.9);
    color: white;
}

.btn-clear-filter {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    font-weight: 600;
    font-size: 13px;
    padding: 0 20px;
    height: 40px;
    display: flex;
    align-items: center;
    border-radius: 8px;
}

.btn-clear-filter:hover {
    background-color: #f1f5f9;
    color: #1e293b;
}

/* Count display styles for new columns */
.amount-positive-text {
    font-weight: 700;
    color: #3CAA94;
    display: block;
    text-align: center;
}

.member-count-text {
    font-weight: 700;
    color: #6366f1;
    display: block;
    text-align: center;
}

.date-text {
    font-size: 13px;
    color: #94a3b8;
    white-space: nowrap;
}

.branch-text {
    font-size: 13px;
    color: #737c8a;
}

/* Flatpickr Override */
.flatpickr-input {
    width: 100%;
    font-weight: 600;
    font-size: 14px;
    border: 1px solid #e2e8f0;
    height: 46px;
    border-radius: 8px;
    padding: 0.375rem 0.75rem;
    background-color: #fff !important;
}


/* Button Back */
.btn-back {
    background: white;
    color: #6b7280;
    border: 1px solid #d1d5db;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-back:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

/* Alert */
.alert-danger {
    background: #fee2e2;
    color: #991b1b;
    border-radius: 8px;
    border: none;
    padding: 1rem 1.25rem;
    margin-bottom: 1.5rem;
}

/* Loading */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
}

.loading-text {
    margin-top: 1rem;
    color: #6b7280;
    font-size: 0.875rem;
}

/* Detail Content */
.detail-content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

/* Info Card */
.info-card {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    overflow: hidden;
}

.info-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
}

.info-header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.info-header-left i {
    color: var(--primary-color, #3b82f6);
    font-size: 1.25rem;
}

.info-header h5 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
}

.info-body {
    padding: 1.5rem;
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.info-item label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
    margin: 0;
}

.info-value {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9375rem;
    color: #374151;
}

.info-value i {
    font-size: 1rem;
}

/* Campaigns Card */
.campaigns-card {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    overflow: hidden;
}

.campaigns-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
}

.campaigns-header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.campaigns-header-left i {
    color: var(--primary-color, #3b82f6);
    font-size: 1.25rem;
}

.campaigns-header h5 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
}

.campaign-count {
    background: var(--primary-color, #3b82f6);
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.875rem;
    font-weight: 600;
}

/* Button Primary Action */
.btn-primary-action {
    background: var(--primary-color, #3b82f6);
    color: white;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-primary-action:hover {
    background: var(--primary-color-dark, #2563eb);
    transform: translateY(-1px);
}

/* Campaigns Body */
.campaigns-body {
    padding: 1.5rem;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 4rem 2rem;
    color: #9ca3af;
}

.empty-state i {
    margin-bottom: 1rem;
    opacity: 0.5;
}

.empty-state h5 {
    color: #374151;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.empty-state p {
    color: #6b7280;
    font-size: 0.875rem;
}

/* Table */
.table-wrapper {
    overflow-x: auto;
}

.report-list-table {
    white-space: nowrap;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
}

.report-list-table th,
.report-list-table td {
    padding: 12px 16px !important;
    vertical-align: middle;
}

.report-row-clickable {
    cursor: pointer;
    transition: background-color 0.2s;
}

.report-row-clickable:hover {
    background-color: #fff7f7 !important;
}

.modern-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.modern-table thead th {
    background: #f9fafb;
    color: #6b7280;
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.75rem 1rem;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
}

.modern-table tbody tr {
    border-bottom: 1px solid #f3f4f6;
    transition: background-color 0.15s ease;
}

.modern-table tbody tr:hover {
    background-color: #f9fafb;
}

.modern-table tbody td {
    padding: 1rem;
    font-size: 0.875rem;
    color: #374151;
}

.col-number {
    width: 60px;
    color: #9ca3af;
    font-weight: 500;
}

.col-name .name-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.col-name .fw-semibold {
    color: #111827;
}

/* Status Badge */
.status-badge-small {
    padding: 0.25rem 0.625rem;
    border-radius: 12px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;
}

.status-badge-modal {
    padding: 0.375rem 0.875rem;
    border-radius: 14px;
    font-size: 0.8125rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.status-active {
    background: #d1fae5;
    color: #065f46;
}

.status-expired {
    background: #fee2e2;
    color: #991b1b;
}

.status-upcoming {
    background: #dbeafe;
    color: #1e40af;
}

.badge-type {
    display: inline-block;
    padding: 0.375rem 0.75rem;
    background: var(--secondary-color, #eff6ff);
    color: var(--primary-color, #3b82f6);
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;
}

.col-business {
    color: #374151;
}

.code-wrapper {
    display: flex;
    align-items: center;
}

.referral-code {
    background: #fff3f5;
    color: #374151;
    padding: 0.375rem 0.75rem;
    border-radius: 6px;
    font-size: 0.8125rem;
    font-family: 'Monaco', 'Courier New', monospace;
    font-weight: 600;
}

.col-date {
    color: #6b7280;
    font-size: 0.8125rem;
}

/* Action Buttons */
.action-buttons {
    display: flex;
    gap: 0.5rem;
}

.btn-action {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
}

.btn-action-analytics {
    background: #dcfce7;
    color: #16a34a;
}

.btn-action-analytics:hover {
    background: #bbf7d0;
}

.btn-action-view {
    background: #eff6ff;
    color: #3b82f6;
}

.btn-action-view:hover {
    filter: brightness(0.9);
}

.btn-action-edit {
    background: #fef3c7;
    color: #f59e0b;
}

.btn-action-edit:hover {
    background: #fde68a;
}

.btn-action-delete {
    background: #fee2e2;
    color: #ef4444;
}

.btn-action-delete:hover {
    background-color: #fee2e2;
    color: #b91c1c;
}

/* Summary Cards */
.total-summary {
    padding: 0 10px;
}

.summary-card {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
    height: 100%;
}

.summary-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.summary-content {
    display: flex;
    align-items: center;
    gap: 16px;
}

.summary-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 20px;
    flex-shrink: 0;
}

.icon-events {
    background-color: #3CAA94;
}

.icon-members {
    background-color: #6366f1;
}

.summary-details {
    flex: 1;
    min-width: 0;
    text-align: left;
    margin-left: 6px;
}

.summary-label-text {
    font-size: 12px;
    font-weight: 600;
    color: #6c757d;
    margin: 0 0 2px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.summary-value-text {
    font-size: 22px;
    font-weight: 700;
    margin: 0;
    line-height: 1.2;
    color: #212529;
}

.total-events {
    font-weight: 700;
    color: #3CAA94;
}

.total-members {
    font-weight: 700;
    color: #6366f1;
}

/* Modal Header with Badge */
.modal-header-with-badge {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 1rem;
}

/* Campaign Details Modal */
.campaign-details {
    padding: 1rem 0;
}

.campaign-details-wide {
    padding: 0;
}

/* Modal Wide */
:deep(.modal-wide .modal-dialog) {
    max-width: 800px !important;
}

/* Detail Section */
.detail-section {
    padding: 1.5rem;
    border-bottom: 1px solid #f3f4f6;
}

.detail-section:last-child {
    border-bottom: none;
}

/* Detail Grid Two Columns */
.detail-grid-two {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
}

/* Detail Item Modern - Clean Style */
.detail-item-modern {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
}

.detail-item-modern label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #6b7280;
    margin: 0;
}

.detail-item-modern label i {
    color: var(--primary-color, #3b82f6);
    font-size: 0.875rem;
}

.value-text {
    color: #111827;
    font-size: 0.9375rem;
    padding: 0.5rem 0;
}

.badge-type-clean {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 0.875rem;
    background: var(--secondary-color, #eff6ff);
    color: var(--primary-color, #3b82f6);
    border-radius: 6px;
    font-size: 0.8125rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    align-self: flex-start;
}

.link-external-clean {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--primary-color, #3b82f6);
    text-decoration: none;
    font-size: 0.875rem;
    word-break: break-all;
}

.link-external-clean:hover {
    text-decoration: underline;
}

.link-external-clean i {
    flex-shrink: 0;
    font-size: 0.75rem;
}

/* Share Section */
.share-section {
    background: #f9fafb;
    border-top: 3px solid var(--primary-color, #3b82f6);
}

.share-row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
}

.share-item {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
}

.share-item label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #374151;
    margin: 0;
}

.share-item label i {
    color: var(--primary-color, #3b82f6);
}

/* Input Copy Group */
.input-copy-group {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: stretch;
    height: 44px;
}

.input-clean {
    flex: 1;
    padding: 0 1rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: white;
    color: #374151;
    font-size: 0.875rem;
    font-family: 'Monaco', 'Courier New', monospace;
    outline: none;
    transition: border-color 0.2s ease;
    height: 100%;
    min-width: 0;
}

.input-clean:focus {
    border-color: var(--primary-color, #3b82f6);
    outline: 2px solid rgba(59, 130, 246, 0.1);
}

.code-clean {
    flex: 1;
    display: flex;
    align-items: center;
    padding: 0 1rem;
    background: white;
    border: 1px solid #d1d5db;
    color: #374151;
    border-radius: 8px;
    font-size: 0.9375rem;
    font-family: 'Monaco', 'Courier New', monospace;
    font-weight: 600;
    letter-spacing: 0.025em;
    height: 100%;
}

.btn-copy-clean {
    background: var(--primary-color, #3b82f6);
    color: white;
    border: none;
    padding: 0 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;
    height: 100%;
    flex-shrink: 0;
}

.btn-copy-clean:hover {
    background: var(--primary-color-dark, #2563eb);
}

.share-info {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.875rem 1rem;
    background: rgba(59, 130, 246, 0.05);
    border-left: 3px solid var(--primary-color, #3b82f6);
    border-radius: 6px;
    color: #1e40af;
    font-size: 0.8125rem;
    margin-top: 0.5rem;
}

.share-info i {
    font-size: 0.875rem;
    flex-shrink: 0;
}

/* Old styles - keep for backward compatibility */
.link-input {
    flex: 1;
    border: none;
    background: transparent;
    color: #374151;
    font-size: 0.875rem;
    font-family: 'Monaco', 'Courier New', monospace;
    outline: none;
}

.link-input:focus {
    outline: none;
}

.btn-copy-link {
    background: var(--primary-color, #3b82f6);
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;
}

.btn-copy-link:hover {
    background: var(--primary-color-dark, #2563eb);
    transform: translateY(-1px);
}

.share-hint {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin-top: 0.5rem;
    padding: 0.75rem 1rem;
    background: rgba(59, 130, 246, 0.1);
    border-radius: 8px;
    color: #1e40af;
    font-size: 0.8125rem;
}

.share-hint i {
    margin-top: 2px;
    font-size: 0.875rem;
}

/* Code Share Box */
.code-share-box {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
}

.referral-code-display {
    flex: 1;
    background: #f3f4f6;
    color: #374151;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    font-size: 1.125rem;
    font-family: 'Monaco', 'Courier New', monospace;
    font-weight: 700;
    letter-spacing: 0.05em;
}

.btn-copy-code {
    background: white;
    color: var(--primary-color, #3b82f6);
    border: 2px solid var(--primary-color, #3b82f6);
    padding: 0.75rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;
}

.btn-copy-code:hover {
    background: var(--primary-color, #3b82f6);
    color: white;
}

/* Link External Modern */
.link-external-modern {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    color: var(--primary-color, #3b82f6);
    text-decoration: none;
    font-size: 0.875rem;
    transition: all 0.2s ease;
    word-break: break-all;
}

.link-external-modern:hover {
    background: #f9fafb;
    border-color: var(--primary-color, #3b82f6);
}

.link-external-modern i {
    flex-shrink: 0;
}

/* Old Modal Styles (keep for backward compatibility) */
.detail-row {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem 0;
    border-bottom: 1px solid #f3f4f6;
}

.detail-row:last-child {
    border-bottom: none;
}

.detail-row label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
}

.detail-row label i {
    color: var(--primary-color, #3b82f6);
}

.detail-row span {
    color: #374151;
    font-size: 0.9375rem;
}

.code-copy-wrapper {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.referral-code-large {
    flex: 1;
    background: #f3f4f6;
    color: #374151;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    font-size: 1rem;
    font-family: 'Monaco', 'Courier New', monospace;
    font-weight: 600;
}

.btn-copy {
    background: var(--primary-color, #3b82f6);
    color: white;
    border: none;
    padding: 0.75rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;
}

.btn-copy:hover {
    background: var(--primary-color-dark, #2563eb);
}

/* Refresh button */
.btn-refresh {
    background: white;
    color: var(--primary-color, #3b82f6);
    border: 1px solid #e5e7eb;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-refresh:hover {
    background: #f9fafb;
    border-color: var(--primary-color, #3b82f6);
}

.link-external {
    color: var(--primary-color, #3b82f6);
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9375rem;
}

.link-external:hover {
    text-decoration: underline;
}

/* Responsive */
@media (max-width: 768px) {
    :deep(.modal-wide .modal-dialog) {
        max-width: 95% !important;
        margin: 1rem auto;
    }

    .detail-section {
        padding: 1rem;
    }

    .detail-grid-two {
        grid-template-columns: 1fr;
    }

    .share-row {
        grid-template-columns: 1fr;
        gap: 1rem;
    }

    .input-copy-group {
        flex-direction: row !important;
        height: 44px;
    }

    .input-clean,
    .code-clean {
        height: 44px;
    }

    .btn-copy-clean {
        height: 44px;
        padding: 0 1rem;
    }

    .campaigns-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }

    .btn-primary-action {
        width: 100%;
        justify-content: center;
    }

    .info-grid {
        grid-template-columns: 1fr;
    }

    .action-buttons {
        flex-direction: column;
        gap: 0.25rem;
    }

    .btn-action {
        width: 100%;
        height: 32px;
        justify-content: center;
    }

    .code-copy-wrapper {
        flex-direction: column;
        align-items: stretch;
    }

    .btn-copy {
        width: 100%;
        justify-content: center;
    }
}

@media (max-width: 480px) {
    .info-header h5,
    .campaigns-header h5 {
        font-size: 1rem;
    }

    .modern-table {
        font-size: 0.8125rem;
    }

    .modern-table thead th {
        padding: 0.5rem;
        font-size: 0.6875rem;
    }

    .modern-table tbody td {
        padding: 0.75rem 0.5rem;
    }
}
</style>
